import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ANALYTICS_EVENT } from '@/common/constants/analyticsEvents';
import { useAnalytics } from '@/common/contexts/AnalyticsContext';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import StoryViewer from './StoryViewer';
import StoryAvatar from './components/StoryAvatar';
import StoriesBarSkeleton from './components/StoriesBarSkeleton';
import StoriesBarStrip from './components/StoriesBarStrip';
import { useStoriesBar } from './hooks/useStoriesBar';
import {
	useStoryMediaPreload,
	useStoriesBarPreload,
} from './hooks/useStoryMediaPreload';
import { StoriesBarSection } from './storiesBar.styles';

const StoriesBar = ({ persons, isLoading }) => {
	const t = useLanguageData('home');
	const { trackAnalytics } = useAnalytics();
	const {
		storyOpen,
		storyPersons,
		activePersonIdx,
		activeStoryIdx,
		slideState,
		slideAnimating,
		isSliding,
		handleOpenStory,
		handleNextStory,
		handlePrevStory,
		handlePeekPrev,
		handlePeekNext,
		handleSlideComplete,
		handleCloseStory,
	} = useStoriesBar(persons);

	const getStoryAdvanceSource = (payload) => {
		if (payload && typeof payload === 'object' && payload.source) {
			return payload.source;
		}

		return 'manual';
	};

	const trackStoryAdvance = (eventName, source) => {
		const activePerson = storyPersons[activePersonIdx];

		trackAnalytics(eventName, {
			personId: activePerson?.personId ?? null,
			storyIndex: activeStoryIdx,
			source,
		});
	};

	const handleOpenStoryWithAnalytics = (personId) => {
		trackAnalytics(ANALYTICS_EVENT.HOME.STORY_OPEN, {
			personId: personId ?? null,
		});
		handleOpenStory(personId);
	};

	const handleNextStoryWithAnalytics = (payload) => {
		trackStoryAdvance(
			ANALYTICS_EVENT.HOME.STORY_NEXT,
			getStoryAdvanceSource(payload),
		);
		handleNextStory();
	};

	const handlePrevStoryWithAnalytics = (payload) => {
		trackStoryAdvance(
			ANALYTICS_EVENT.HOME.STORY_PREV,
			getStoryAdvanceSource(payload),
		);
		handlePrevStory();
	};

	const handlePeekNextWithAnalytics = () => {
		trackStoryAdvance(ANALYTICS_EVENT.HOME.STORY_NEXT, 'peek');
		handlePeekNext();
	};

	const handlePeekPrevWithAnalytics = () => {
		trackStoryAdvance(ANALYTICS_EVENT.HOME.STORY_PREV, 'peek');
		handlePeekPrev();
	};

	useStoriesBarPreload(persons);
	useStoryMediaPreload(storyPersons, activePersonIdx, {
		open: storyOpen,
		preloadPeeks: storyPersons.length > 0,
	});

	useEffect(() => {
		if (!storyOpen) {
			return undefined;
		}

		const { body, documentElement } = document;
		const previousBodyOverflow = body.style.overflow;
		const previousHtmlOverflow = documentElement.style.overflow;

		body.style.overflow = 'hidden';
		documentElement.style.overflow = 'hidden';

		return () => {
			body.style.overflow = previousBodyOverflow;
			documentElement.style.overflow = previousHtmlOverflow;
		};
	}, [storyOpen]);

	const showStripSkeleton = isLoading && persons.length === 0;
	const showBar = showStripSkeleton || persons.length > 0;

	if (!showBar) {
		return null;
	}

	const stripContent = showStripSkeleton ? (
		<StoriesBarStrip itemCount={0}>
			<StoriesBarSkeleton />
		</StoriesBarStrip>
	) : (
		<StoriesBarStrip itemCount={persons.length}>
			{persons.map((person) => (
				<StoryAvatar
					key={person._id || person.id}
					person={person}
					onOpen={handleOpenStoryWithAnalytics}
				/>
			))}
		</StoriesBarStrip>
	);

	return (
		<>
			<StoriesBarSection aria-label={t.stories}>{stripContent}</StoriesBarSection>
			{typeof document !== 'undefined'
				&& createPortal(
					<StoryViewer
						open={storyOpen}
						storyPersons={storyPersons}
						activePersonIdx={activePersonIdx}
						activeStoryIdx={activeStoryIdx}
						slideState={slideState}
						slideAnimating={slideAnimating}
						isSliding={isSliding}
						onClose={handleCloseStory}
						onNextStory={handleNextStoryWithAnalytics}
						onPrevStory={handlePrevStoryWithAnalytics}
						onPeekPrev={handlePeekPrevWithAnalytics}
						onPeekNext={handlePeekNextWithAnalytics}
						onSlideComplete={handleSlideComplete}
					/>,
					document.body,
				)}
		</>
	);
};

export default StoriesBar;
